/**
 * TasksController — /api/v1/tasks
 * Mirrors: src/Aidepo.Api/Controllers/TasksController.cs
 *
 * The unified maintenance timeline (Phase 3.2, beads hh-fdp). Tasks are a
 * projection over ScheduleRule + SeasonalItem — there is no Task table. The
 * composite id carries its source: `schedule:{guid}` / `seasonal:{guid}`.
 *   - schedule: isDone advances NextAt by PeriodMonths; snoozeUntil sets NextAt.
 *   - seasonal: isDone marks the SeasonalItem; snoozeUntil is rejected (422).
 */
import { apiGet, apiPatch } from '../client.js';
import type { IsoDateTime } from '../types.js';

/** Source discriminant carried in the composite task id prefix. */
export type TaskSource = 'schedule' | 'seasonal';

/**
 * One task on the timeline. `id` is composite — `schedule:{guid}` or
 * `seasonal:{guid}` — and must be passed verbatim back to PATCH.
 */
export interface TaskRow {
  readonly id: string;
  readonly date: IsoDateTime;
  readonly title: string;
  readonly equipmentId: string | null;
  readonly isSafety: boolean;
  readonly isSeasonal: boolean;
  readonly isDone: boolean;
  readonly source: TaskSource;
  /**
   * Дата последнего выполненного ТО для регламента (aidepo-57.4), или
   * null/undefined для сезонных пунктов и для ТО, которое ещё не делали.
   */
  readonly lastCompletedAt?: IsoDateTime | null;
}

export interface PatchTaskRequest {
  readonly isDone?: boolean;
  readonly snoozeUntil?: IsoDateTime;
}

/**
 * GET /api/v1/tasks?from=&to= — maintenance + seasonal tasks in a date range.
 * The controller responds with a bare JSON array of TaskDto. Both bounds are
 * optional server-side (default −1mo … +6mo); we always pass them.
 */
export function listTasks(from: IsoDateTime, to: IsoDateTime): Promise<readonly TaskRow[]> {
  const qs = new URLSearchParams({ from, to }).toString();
  return apiGet<readonly TaskRow[]>(`/api/v1/tasks?${qs}`);
}

/**
 * PATCH /api/v1/tasks/{id} — mark done or snooze. `id` is the composite id
 * straight from a TaskRow. Returns the mutation payload (shape varies by
 * source) — callers typically ignore it and refetch.
 */
export function patchTask(id: string, req: PatchTaskRequest): Promise<unknown> {
  return apiPatch<unknown, PatchTaskRequest>(`/api/v1/tasks/${encodeURIComponent(id)}`, req);
}
